import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../contexts';

const TIMEOUT_DURATION = 15 * 60 * 1000; // 15 min
const WARNING_BEFORE = 1 * 60 * 1000;    // warn 1 min before timeout

export const useSessionTimeout = () => {
  const { user, signOut } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const lastActivityRef = useRef(Date.now());
  const signOutRef = useRef(signOut);
  const userRef = useRef(user);
  const intervalRef = useRef(null);

  // Keep refs current without triggering re-runs
  useEffect(() => { signOutRef.current = signOut; }, [signOut]);
  useEffect(() => { userRef.current = user; }, [user]);

  const handleLogout = useCallback(async () => {
    const failsafe = setTimeout(() => {
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/login?timeout=true';
    }, 2000);
    try {
      await signOutRef.current();
      clearTimeout(failsafe);
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/login?timeout=true';
    } catch {
      clearTimeout(failsafe);
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/login?timeout=true';
    }
  }, []);

  const extendSession = useCallback(() => {
    lastActivityRef.current = Date.now();
    setShowWarning(false);
  }, []);

  useEffect(() => {
    if (!user) {
      setShowWarning(false);
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }

    lastActivityRef.current = Date.now();

    const updateActivity = () => {
      lastActivityRef.current = Date.now();
      setShowWarning(false);
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => document.addEventListener(e, updateActivity));

    // Check every 30 seconds instead of relying on setTimeout cascade
    intervalRef.current = setInterval(() => {
      if (!userRef.current) return;
      const idle = Date.now() - lastActivityRef.current;
      if (idle >= TIMEOUT_DURATION) {
        handleLogout();
      } else if (idle >= TIMEOUT_DURATION - WARNING_BEFORE) {
        setShowWarning(true);
      } else {
        setShowWarning(false);
      }
    }, 30000);

    return () => {
      events.forEach(e => document.removeEventListener(e, updateActivity));
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [user, handleLogout]);

  return { showWarning, extendSession, handleLogout };
};
